/**
 * Corporate Memory Page
 * 
 * View and manage AI-generated corporate knowledge and insights
 */

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../state/language/LanguageProvider';

interface CorporateMemory {
  id: string;
  memoryType: string;
  title: string;
  description: string;
  outcome: 'positive' | 'negative' | 'neutral';
  confidenceScore: number;
  sourceType: string;
  sourceId: string;
  tags: string[];
  impactLevel: 'low' | 'medium' | 'high' | 'critical';
  createdAt: string;
  lastAccessed: string;
}

export const CorporateMemoryPage: React.FC = () => {
  const { t } = useLanguage();
  const [memories, setMemories] = useState<CorporateMemory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterOutcome, setFilterOutcome] = useState<string>('all');
  const [selectedMemory, setSelectedMemory] = useState<CorporateMemory | null>(null);

  useEffect(() => {
    loadMemories();
  }, []);

  const loadMemories = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/v1/ai/memories', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error(t('ai.corporateMemory.errors.failedToLoadMemories'));
      }

      const data = await response.json();
      setMemories(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('ai.corporateMemory.errors.anErrorOccurred'));
    } finally {
      setIsLoading(false);
    }
  };

  const filteredMemories = memories.filter(memory => {
    const matchesSearch = memory.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         memory.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || memory.memoryType === filterType;
    const matchesOutcome = filterOutcome === 'all' || memory.outcome === filterOutcome;
    
    return matchesSearch && matchesType && matchesOutcome;
  });

  const getOutcomeColor = (outcome: string) => {
    switch (outcome) {
      case 'positive': return 'text-green-600 bg-green-100';
      case 'negative': return 'text-red-600 bg-red-100';
      case 'neutral': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'critical': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004C94]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-3xl font-bold text-[#004D99]">
              {t('ai.corporateMemory.title')}
            </h1>
            <p className="mt-2 text-[#95C4FF]">
              {t('ai.corporateMemory.description')}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border mb-6">
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#004D99] mb-2">
                  {t('ai.corporateMemory.search')}
                </label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('ai.corporateMemory.searchPlaceholder')}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#5B94DD] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#004D99] mb-2">
                  {t('ai.corporateMemory.type')}
                </label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#5B94DD] focus:border-transparent"
                >
                  <option value="all">{t('ai.corporateMemory.allTypes')}</option>
                  <option value="success">{t('ai.corporateMemory.success')}</option>
                  <option value="failure">{t('ai.corporateMemory.failure')}</option>
                  <option value="pattern">{t('ai.corporateMemory.pattern')}</option>
                  <option value="insight">{t('ai.corporateMemory.insight')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#004D99] mb-2">
                  {t('ai.corporateMemory.outcome')}
                </label>
                <select
                  value={filterOutcome}
                  onChange={(e) => setFilterOutcome(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#5B94DD] focus:border-transparent"
                >
                  <option value="all">{t('ai.corporateMemory.allOutcomes')}</option>
                  <option value="positive">{t('ai.corporateMemory.positive')}</option>
                  <option value="negative">{t('ai.corporateMemory.negative')}</option>
                  <option value="neutral">{t('ai.corporateMemory.neutral')}</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={loadMemories}
                  className="w-full px-4 py-2 bg-[#004C94] text-white rounded-md hover:bg-[#004D99] transition-colors"
                >
                  {t('ai.corporateMemory.refresh')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-[#004D99]">{memories.length}</div>
            <div className="text-sm text-gray-500">{t('ai.corporateMemory.totalMemories')}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-green-600">
              {memories.filter(m => m.outcome === 'positive').length}
            </div>
            <div className="text-sm text-gray-500">{t('ai.corporateMemory.positiveOutcomes')}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-red-600">
              {memories.filter(m => m.outcome === 'negative').length}
            </div>
            <div className="text-sm text-gray-500">{t('ai.corporateMemory.negativeOutcomes')}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-orange-600">
              {memories.filter(m => m.impactLevel === 'high' || m.impactLevel === 'critical').length}
            </div>
            <div className="text-sm text-gray-500">{t('ai.corporateMemory.highImpact')}</div>
          </div>
        </div>

        {/* Memories List */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-[#004D99]">
              {t('ai.corporateMemory.memories')}
            </h2>
          </div>

          <div className="divide-y divide-gray-200">
            {filteredMemories.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <p>{t('ai.corporateMemory.noMemoriesFound')}</p>
              </div>
            ) : (
              filteredMemories.map((memory) => (
                <div key={memory.id} className="p-6 hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedMemory(memory)}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-[#004D99]">
                          {memory.title}
                        </h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getOutcomeColor(memory.outcome)}`}>
                          {memory.outcome}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getImpactColor(memory.impactLevel)}`}>
                          {memory.impactLevel}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-3">{memory.description}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>{t('ai.corporateMemory.confidence')}: {(memory.confidenceScore * 100).toFixed(0)}%</span>
                        <span>{t('ai.corporateMemory.type')}: {memory.memoryType}</span>
                        <span>{t('ai.corporateMemory.source')}: {memory.sourceType}</span>
                        <span>{t('ai.corporateMemory.created')}: {new Date(memory.createdAt).toLocaleDateString()}</span>
                      </div>
                      {memory.tags.length > 0 && (
                        <div className="mt-2">
                          <div className="flex flex-wrap gap-1">
                            {memory.tags.map((tag, index) => (
                              <span key={index} className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Memory Detail Modal */}
      {selectedMemory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-[#004D99]">{selectedMemory.title}</h2>
                <button
                  onClick={() => setSelectedMemory(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-[#004D99] mb-2">{t('ai.corporateMemory.description')}</h3>
                  <p className="text-gray-700">{selectedMemory.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-semibold text-[#004D99] mb-2">{t('ai.corporateMemory.outcome')}</h3>
                    <span className={`px-2 py-1 text-sm font-medium rounded-full ${getOutcomeColor(selectedMemory.outcome)}`}>
                      {selectedMemory.outcome}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#004D99] mb-2">{t('ai.corporateMemory.impactLevel')}</h3>
                    <span className={`px-2 py-1 text-sm font-medium rounded-full ${getImpactColor(selectedMemory.impactLevel)}`}>
                      {selectedMemory.impactLevel}
                    </span>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-[#004D99] mb-2">{t('ai.corporateMemory.confidenceScore')}</h3>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-[#004C94] h-2 rounded-full" 
                      style={{ width: `${selectedMemory.confidenceScore * 100}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{(selectedMemory.confidenceScore * 100).toFixed(1)}%</p>
                </div>
                {selectedMemory.tags.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-[#004D99] mb-2">{t('ai.corporateMemory.tags')}</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedMemory.tags.map((tag, index) => (
                        <span key={index} className="px-2 py-1 text-sm bg-gray-100 text-gray-600 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
